import { KeyboardBackspace } from "@mui/icons-material";
import {
  Avatar,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { patientApi } from "src/api/services/patientApi";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Paths } from "src/constants/paths";
import { supabaseClient } from "src/lib/supabase/supabaseClient";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/app/store";
import { usePatient } from "src/context/PatientProvider";
import { useTranslation } from "react-i18next";
import { useMessageThreads } from "src/context/MessageThreadsProvider";
import { useGetChargesQuery } from "src/api/services/chargesApi";

const pathsList = [
  {
    name: `account.navBar.account`,
    link: Paths.MY_ACCOUNT_OVERVIEW,
  },
  {
    name: `account.navBar.subscriptions`,
    link: Paths.MY_SUBSCRIPTIONS,
  },
  {
    name: `account.navBar.preferred-pharmacy`,
    link: Paths.MY_ACCOUNT_PREFERRED_PHARMACY,
  },
  {
    name: `account.navBar.transaction-history`,
    link: Paths.MY_ACCOUNT_TRANSACTION_HISTORY,
  },
  {
    name: `account.navBar.insurance-details`,
    link: Paths.MY_ACCOUNT_INSURANCE_DETAILS,
  },
  {
    name: `account.navBar.payment-methods`,
    link: Paths.MY_PAYMENT_METHODS,
  },
  {
    name: `account.navBar.help-center`,
    link: `${Paths.MY_ACCOUNT_HELP_CENTER}?section=Medical+Care`,
  },
  {
    name: `account.navBar.messages`,
    link: "messages",
  },
];

const MobilePatientAccountNav = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { threads } = useMessageThreads(false);
  const firstName = usePatient((patient) => patient.profile.first_name ?? "");
  const lastName = usePatient((patient) => patient.profile.last_name ?? "");
  const email = usePatient((patient) => patient.profile.email);
  const { t } = useTranslation();

  const { data: charges = [] } = useGetChargesQuery();

  const chargeProductInsurance = useMemo(
    () => charges.find((c) => c.product.insurance_based),
    [charges],
  );

  const chargeProductEPrescribe = useMemo(
    () => charges.find((c) => c.product.is_e_prescribe),
    [charges],
  );

  const patientInitials = useMemo(() => {
    return `${firstName?.slice(0, 1)}${lastName?.slice(0, 1)}`;
  }, [firstName, lastName]);

  const filteredPaths = useMemo(() => {
    const filtered = pathsList.filter((p) => {
      if (p.name === "account.navBar.preferred-pharmacy") {
        return chargeProductEPrescribe != null;
      }

      if (p.name === "account.navBar.insurance-details") {
        return chargeProductInsurance != null;
      }

      if (p.name === "account.navBar.messages") {
        return threads.length > 0;
      }

      return true;
    });

    return filtered.filter((path) => path.name !== `account.navBar.messages`);
  }, [chargeProductEPrescribe, chargeProductInsurance, threads.length]);

  const handleLogout = useCallback(async () => {
    try {
      await supabaseClient.auth.signOut();
      //these calls clear out the cached data from the rtk apis,
      dispatch(patientApi.util.resetApiState());
      navigate("/login");
    } catch (err) {
      console.log("Logout failed:", err);
    }
  }, [dispatch, navigate]);

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center">
        <IconButton
          sx={{ color: "#00B6B8" }}
          onClick={() => navigate(Paths.PATIENT_DASHBOARD)}
        >
          <KeyboardBackspace fontSize="large" />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>
          {t(`account.navBar.account`)}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1}>
        <Avatar
          sx={{
            bgcolor: "#00B6B8",
            width: 70,
            height: 70,
            p: 1,
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            {patientInitials}
          </Typography>
        </Avatar>
        <Stack gap={1}>
          <Typography fontWeight={600}>{firstName + " " + lastName}</Typography>
          <Typography variant="subtitle2">{email}</Typography>
        </Stack>
      </Stack>
      <List data-testid="navigation-list">
        {filteredPaths.map((path) => (
          <ListItemButton
            key={path.name}
            component={Link}
            to={path.link}
            sx={{
              borderTop: "1px solid var(--Stroke-Primary, #E8F2F2)",
              borderBottom: "1px solid var(--Stroke-Primary, #E8F2F2)",
              padding: "16px 24px",
            }}
          >
            <ListItemText
              primary={t(path.name)}
              slotProps={{ primary: { fontWeight: 600 } }}
            />
            <NavigateNextIcon sx={{ color: "#00B6B8" }} />
          </ListItemButton>
        ))}
      </List>
      <Button variant="secondary" sx={{ mt: 1 }} onClick={handleLogout}>
        {t(`account.navBar.logout`)}
      </Button>
    </Stack>
  );
};

export default MobilePatientAccountNav;
